import { useMemo, useState } from "react";
import { GitBranch, Tag, TreeStructure } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import {
  Badge,
  type BadgeVariant,
  DataRow,
  DataTable,
  PageHeader,
} from "@breakingthelines/design-system";

import { listTaxonomyNodes } from "@/lib/admin-api";

import styles from "./taxonomy-page.module.css";

const TABLE_COLUMNS =
  "minmax(220px, 1.5fr) minmax(120px, 0.7fr) minmax(140px, 0.8fr) minmax(120px, 0.6fr)";

function readableKind(kind?: string) {
  return (kind ?? "TAXONOMY_KIND_UNSPECIFIED")
    .replace("TAXONOMY_KIND_", "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (char) => char.toUpperCase());
}

function statusVariant(status?: string): BadgeVariant {
  if (status === "TAXONOMY_NODE_STATUS_ACTIVE") return "tintedSuccess";
  if (status === "TAXONOMY_NODE_STATUS_DEPRECATED") return "secondary";
  if (status === "TAXONOMY_NODE_STATUS_HIDDEN") return "tintedWarn";
  return "tintedNeutral";
}

export function TaxonomyPage() {
  const [query, setQuery] = useState("");
  const { data, isLoading, error } = useQuery({
    queryKey: ["taxonomy", "nodes", query],
    queryFn: () => listTaxonomyNodes({ query, pageSize: 100 }),
  });

  const nodes = data?.nodes ?? [];
  const total = data?.pageInfo?.totalCount ?? nodes.length;
  const kindCount = useMemo(() => new Set(nodes.map((node) => node.kind)).size, [nodes]);
  const activeCount = nodes.filter((node) => node.status === "TAXONOMY_NODE_STATUS_ACTIVE").length;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Taxonomy"
        kicker="BTL Admin"
        description="Govern canonical entities, aliases, and global search facets. Local tags stay in Studio; this surface owns the platform vocabulary."
      />

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <Tag size={18} />
          <p className={styles.summaryLabel}>Canonical nodes</p>
          <p className={styles.summaryValue}>{total}</p>
        </div>
        <div className={styles.summaryCard}>
          <TreeStructure size={18} />
          <p className={styles.summaryLabel}>Kinds represented</p>
          <p className={styles.summaryValue}>{kindCount}</p>
        </div>
        <div className={styles.summaryCard}>
          <GitBranch size={18} />
          <p className={styles.summaryLabel}>Active nodes</p>
          <p className={styles.summaryValue}>{activeCount}</p>
        </div>
      </div>

      <div className={styles.toolbar}>
        <input
          className={styles.searchInput}
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          placeholder="Search taxonomy nodes"
        />
        <p className={styles.muted}>Global governance. Use Studio for scoped local tags.</p>
      </div>

      {error instanceof Error ? <div className={styles.emptyState}>{error.message}</div> : null}

      <DataTable columns={TABLE_COLUMNS} header={["Node", "Kind", "Slug", "Status"]}>
        {isLoading ? (
          <DataRow className={styles.tableRow}>
            <span className={styles.muted}>Loading taxonomy nodes...</span>
            <span />
            <span />
            <span />
          </DataRow>
        ) : nodes.length === 0 ? (
          <DataRow className={styles.tableRow}>
            <span className={styles.muted}>No taxonomy nodes yet.</span>
            <span />
            <span />
            <span />
          </DataRow>
        ) : (
          nodes.map((node) => (
            <DataRow key={node.id} className={styles.tableRow}>
              <span>
                <span className={styles.nodeTitle}>{node.label}</span>
                {node.description ? (
                  <span className={styles.nodeMeta}>{node.description}</span>
                ) : null}
              </span>
              <span className={styles.muted} data-label="Kind">
                {readableKind(node.kind)}
              </span>
              <span className={styles.muted} data-label="Slug">
                /{node.slug}
              </span>
              <Badge variant={statusVariant(node.status)}>
                {node.status?.replace("TAXONOMY_NODE_STATUS_", "").toLowerCase() ?? "unknown"}
              </Badge>
            </DataRow>
          ))
        )}
      </DataTable>
    </div>
  );
}
